import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';

import {
  SUPPORTED_S3_UPLOAD_CONTENT_TYPES,
  SupportedUploadContentTypes
} from '@/api/s3UploadFileTypes';
import { ScreenerSuggestion } from '@/types/common';
import {
  AdmissionStatus,
  AdmissionStatusCode,
  AiSuggestionSource,
  ExternalEhrPatient,
  FacilityFacilityTypeEnum,
  FacilityIntegrationNameEnum,
  Lead,
  LeadAdmissionFlagsInner,
  LeadAdmissionStatusInfo,
  LeadCareAttributesInner,
  LeadCareAttributesInnerCategoryEnum,
  LeadConnectorEnum,
  LeadMedicareEstimatesStatusEnum,
  LeadScreenerAdditionalDocumentsInner,
  LeadScreenerLeadTagsInner,
  LeadSourceMergedInto,
  LeadStatus,
  LeadTimData,
  LeadWarning,
  LeadWarningTypeEnum,
  PlaceholderField,
  ScreenerDocumentErrorErrorTypeEnum,
  ScreenerSpecialCriteria,
  ScreenerSpecialCriteriaIdentifierEnum,
  SourceContact,
  User
} from '@/types/crm-screener';
import { Occupancy } from '@/types/crm-screener-api';
import { getFromNowFormattedDate } from '@/utils/date';
import { getFullName, NameFormat } from '@/utils/getFullName';

import { AnalysisResultModel } from './AnalysisResultModel';
import { DocumentErrorModel } from './DocumentErrorModel';
import { DocumentModel, DocumentStatus } from './DocumentModel';
import {
  CheckName,
  CheckRunModel,
  EffectiveDecisionModel
} from './EffectiveDecisionModel';
import { LeadInfoModel } from './LeadInfoModel';
import { LeadWaitlistModel } from './LeadWaitlistModel';
import { ScreenerInsuranceModel } from './ScreenerInsuranceModel';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 min',
    mm: '%d mins',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years'
  }
});

// Real status name values in the DB for a referral's status
export enum LeadStatusName {
  YesAccept = 'Yes, Accept',
  NoDecline = 'No, Decline',
  NeedMoreInfo = 'Need More Info',
  InterestedNeedMoreInfo = 'Interested, Need More Info',
  ReferralReceived = 'Referral Received',
  New = 'New'
}

// Enum used to display a shorter name for status
export enum LeadModelStatusName {
  New = 'New',
  ReferralReceived = 'Referral Received',
  InterestedNeedMoreInfo = 'Need More Info',
  YesAccept = 'Accept',
  NoDecline = 'Decline'
}

// Map from real status name to shorter version
export const LeadModelStatusShortMapping = {
  'Yes, Accept': LeadModelStatusName.YesAccept,
  'No, Decline': LeadModelStatusName.NoDecline,
  'Need More Info': LeadModelStatusName.InterestedNeedMoreInfo,
  'Interested, Need More Info': LeadModelStatusName.InterestedNeedMoreInfo,
  'Referral Received': LeadModelStatusName.ReferralReceived,
  New: LeadModelStatusName.New
};

export const getLeadModelStatusName = (statusName?: string) => {
  if (!statusName) {
    return undefined;
  }

  return (
    LeadModelStatusShortMapping[
      statusName as keyof typeof LeadModelStatusShortMapping
    ] ?? statusName
  );
};

export const isActiveLeadStatusName = (statusName?: string) => {
  const modelStatusName = getLeadModelStatusName(statusName);
  return !!modelStatusName && modelStatusName !== LeadModelStatusName.NoDecline;
};

export const SUPPORTED_CONTENT_TYPES: SupportedUploadContentTypes =
  SUPPORTED_S3_UPLOAD_CONTENT_TYPES;

export const SUPPORTED_STORAGE_DOCUMENT_CONTENT_TYPES: SupportedUploadContentTypes =
  {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg']
  };

const combinableConnectors: LeadConnectorEnum[] = [
  LeadConnectorEnum.ManualUpload,
  LeadConnectorEnum.ManualCopy,
  LeadConnectorEnum.Roc,
  LeadConnectorEnum.Fax
];

const CARE_ATTRIBUTE_CATEGORY_DISPLAY_ORDER: Partial<
  Record<FacilityFacilityTypeEnum, LeadCareAttributesInnerCategoryEnum[]>
> = {
  [FacilityFacilityTypeEnum.SkilledNursing]: [
    LeadCareAttributesInnerCategoryEnum.TypeOfCare,
    LeadCareAttributesInnerCategoryEnum.Program,
    LeadCareAttributesInnerCategoryEnum.ClinicalCareNeed
  ],
  [FacilityFacilityTypeEnum.HomeHealth]: [
    LeadCareAttributesInnerCategoryEnum.Discipline,
    LeadCareAttributesInnerCategoryEnum.ClinicalCareNeed
  ]
};

export class LeadModel implements Lead {
  id: string;
  organization_id: string;
  source_id: string;
  facility_id: string;
  original_facility_id?: string | null;
  first_name: string;
  source_name?: string;
  source_merged_into?: LeadSourceMergedInto;
  source_deleted?: boolean;
  placeholder_fields?: PlaceholderField[];
  last_name: string;
  lead_received_date: string;
  created_at: string;
  screener_suggestion?: ScreenerSuggestion;
  ai_suggestion?: ScreenerSuggestion;
  ai_suggestion_source?: AiSuggestionSource | null;
  effective_decision?: EffectiveDecisionModel;
  status_id: string;
  status?: LeadStatus;
  auto_accepted_by_ai?: boolean;
  ai_accept_outcome?: Lead['ai_accept_outcome'];
  documents: DocumentModel[];
  private unprocessableDocuments: DocumentModel[];
  private recoveredDocuments: DocumentModel[];
  private erroredDocuments: DocumentModel[];
  document_errors?: DocumentErrorModel[] | undefined;
  analysis_results?: Array<AnalysisResultModel>;
  report_s3_access_url?: string;
  special_criteria?: Array<ScreenerSpecialCriteria>;
  external_ehr_patient_id?: string | undefined;
  pcc_pending_patient_id?: string | undefined;
  source_comments_received_at?: string | undefined;
  comments_summary?: string | undefined;
  original_move_in_date?: string;
  pcc_push_by_user?: User | undefined;
  pcc_push_date?: string | undefined;
  connector?: LeadConnectorEnum | undefined;
  connector_referral_id?: string | undefined;
  connector_no_sync: boolean;
  ssn?: string;
  dob?: string;
  medicaid_number?: string;
  medicare_number?: string;
  gender?: string;
  source_contact?: SourceContact | undefined;
  source_contact_id?: string | null | undefined;
  source_discharge_date?: string | null | undefined;
  failed_review_stage_status_id?: string | null | undefined;
  bd_action_failed?: boolean | null | undefined;
  is_visual_references_ready?: boolean | undefined;
  medicare_estimates_visual_refs_ready?: boolean | undefined;
  is_cancelled: boolean;
  duplicate_group_id?: string;
  claimed_by_facility?: {
    id: string;
    name: string;
  };
  facility?: {
    id: string;
    name: string;
    facility_type: FacilityFacilityTypeEnum;
  };
  is_large_document_ai_used?: boolean | undefined;
  medications_extraction_complete?: boolean | undefined;
  is_medication_analysis_completed?: boolean | undefined;
  s_offender_records_stored?: boolean | undefined;
  s_offender_check_failed?: boolean | undefined;
  are_all_documents_analyzed?: boolean | undefined;
  has_insufficient_document_context?: boolean | undefined;
  medicare_estimates_status?: LeadMedicareEstimatesStatusEnum;
  source_comments?: string | null;
  declined_reason?: string | undefined;
  request_info_reason?: string | undefined;
  internal_response_reason?: string | undefined;
  external_response_reason?: string | undefined;
  denial_reason?: string | undefined;
  external_ehr_patient?: ExternalEhrPatient | undefined;
  admission_status?: AdmissionStatus;
  admission_status_code?: Lead['admission_status_code'];
  admission_status_info?: LeadAdmissionStatusInfo;
  previous_admission_status_info?: LeadAdmissionStatusInfo;
  has_uploaded_pcc_initial_details: boolean;
  occupancies?: Array<Occupancy>;
  insurances?: Array<ScreenerInsuranceModel>;
  screener_lead_info?: LeadInfoModel | undefined;
  screener_lead_tags?: LeadScreenerLeadTagsInner[] | undefined;
  care_attributes?: LeadCareAttributesInner[] | undefined;
  tim_data?: LeadTimData | undefined;
  has_no_epic_ehr_access?: boolean | undefined;
  has_start_review_hang?: boolean | undefined;
  admission_flags?: LeadAdmissionFlagsInner[] | undefined;
  waitlist_model?: LeadWaitlistModel | undefined;
  has_new_hospital_messages?: boolean | undefined;
  has_previous_stay_in_facility?: boolean | undefined;
  prev_stay_set_by_user?: User | undefined;
  screener_additional_documents?:
    | LeadScreenerAdditionalDocumentsInner[]
    | undefined;
  lead_warnings?: LeadWarning[] | undefined;
  assignments?: Lead['assignments'];
  home_health_practitioner_assignments?: Lead['home_health_practitioner_assignments'];

  constructor(payload: Lead) {
    Object.assign(this, payload);
    this.analysis_results = payload.analysis_results?.map(
      (item) => new AnalysisResultModel(item)
    );
    this.has_uploaded_pcc_initial_details =
      payload.pcc_pending_patient_id != null;
    this.insurances =
      payload.insurances?.map((item) => new ScreenerInsuranceModel(item)) ?? [];
    if (payload.screener_lead_info) {
      this.screener_lead_info = new LeadInfoModel(payload.screener_lead_info);
    }
    if (payload.effective_decision) {
      this.effective_decision = new EffectiveDecisionModel(
        payload.effective_decision
      );
    }
    if (payload.waitlist) {
      this.waitlist_model = new LeadWaitlistModel(payload.waitlist);
    }

    this.document_errors =
      payload.document_errors?.map((item) => new DocumentErrorModel(item)) ??
      [];

    const documentModels =
      payload.documents?.map((doc) => new DocumentModel(doc)) ?? [];

    this.documents = documentModels.filter(
      (doc) =>
        doc.getStatus() === DocumentStatus.NORMAL ||
        doc.getStatus() === DocumentStatus.NEW
    );

    this.unprocessableDocuments = documentModels.filter(
      (doc) => doc.getStatus() === DocumentStatus.UNPROCESSABLE
    );

    this.recoveredDocuments = documentModels.filter(
      (doc) => doc.getStatus() === DocumentStatus.RECOVERED
    );

    this.erroredDocuments = documentModels.filter(
      (doc) => doc.getStatus() === DocumentStatus.ERROR
    );
  }

  getErroredDocuments(): DocumentModel[] {
    return this.erroredDocuments;
  }

  /** A geo decline is terminal: the analyzer may not overturn it. */
  private isGeoDeclined(): boolean {
    return this.getCheck('geo')?.outcome === ScreenerSuggestion.Reject;
  }

  getEffectiveDecision(): ScreenerSuggestion {
    if (this.isGeoDeclined()) {
      return ScreenerSuggestion.Reject;
    }

    if (this.ai_suggestion !== undefined) {
      return this.ai_suggestion;
    }

    if (this.ai_suggestion_source === AiSuggestionSource.ShadowDecision) {
      return (
        this.effective_decision?.decision ??
        this.screener_suggestion ??
        ScreenerSuggestion.NA
      );
    }

    return this.screener_suggestion ?? ScreenerSuggestion.NA;
  }

  getCareAttributes(category: LeadCareAttributesInnerCategoryEnum) {
    return (
      this.care_attributes?.filter(
        (careAttribute) => careAttribute.category === category
      ) ?? []
    );
  }

  getApplicableCareAttributes() {
    const facilityType = this.facility?.facility_type;
    const categories = (facilityType
      ? CARE_ATTRIBUTE_CATEGORY_DISPLAY_ORDER[facilityType]
      : undefined) ?? [LeadCareAttributesInnerCategoryEnum.ClinicalCareNeed];

    return categories.flatMap((category) => this.getCareAttributes(category));
  }

  getDisplayedSuggestion(): ScreenerSuggestion {
    if (this.isGeoDeclined()) {
      return ScreenerSuggestion.Reject;
    }

    if (this.ai_suggestion_source === AiSuggestionSource.ShadowDecision) {
      return (
        this.ai_suggestion ?? this.screener_suggestion ?? ScreenerSuggestion.NA
      );
    }

    return this.screener_suggestion ?? ScreenerSuggestion.NA;
  }

  getCheck(name: CheckName): CheckRunModel | undefined {
    return this.effective_decision?.getCheck(name);
  }

  isMedicationAnalysisInProgress(): boolean {
    if (this.is_medication_analysis_completed !== false) {
      return false;
    }
    const analyzerOutcome = this.getCheck('analyzer')?.outcome;
    return analyzerOutcome !== undefined && analyzerOutcome !== 'N/A';
  }

  getUnprocessableDocuments(): DocumentModel[] {
    return this.unprocessableDocuments;
  }

  getRecoveredDocuments(): DocumentModel[] {
    return this.recoveredDocuments;
  }

  getNonRecoveredDocumentErrorsByType(): Record<
    ScreenerDocumentErrorErrorTypeEnum,
    DocumentErrorModel[]
  > {
    const errorTypeToIndex: Record<string, number> = {};
    const groupedByType: {
      errorType: ScreenerDocumentErrorErrorTypeEnum;
      errors: DocumentErrorModel[];
    }[] = [];
    const nonRecoveredErrors = this.document_errors?.filter(
      (error) => !error.recovered_at
    );

    nonRecoveredErrors?.forEach((error) => {
      const errorType = error.error_type;
      if (!errorType) {
        return;
      }
      const existingIndex = errorTypeToIndex[errorType];
      const index =
        existingIndex !== undefined
          ? existingIndex
          : groupedByType.push({
              errorType,
              errors: []
            }) - 1;

      errorTypeToIndex[errorType] = index;
      const group = groupedByType[index];
      if (group) {
        group.errors.push(error);
      }
    });

    return groupedByType.reduce(
      (acc, group) => {
        acc[group.errorType] = group.errors;
        return acc;
      },
      {} as Record<ScreenerDocumentErrorErrorTypeEnum, DocumentErrorModel[]>
    );
  }

  getDob(): string | null {
    return this.dob ?? null;
  }

  get fullName(): string {
    if (this.hasPlaceholderName()) {
      if (this.screener_suggestion === ScreenerSuggestion.NA) {
        return 'N/A';
      }
      return this.first_name?.trim() || '';
    }
    return `${this.first_name} ${this.last_name}`.trim();
  }

  getFullName(format: NameFormat = NameFormat.FirstLast): string {
    if (this.hasPlaceholderName()) {
      return this.fullName;
    }
    return getFullName(
      { first_name: this.first_name, last_name: this.last_name },
      format
    );
  }

  public hasPlaceholderField(fieldName: PlaceholderField): boolean {
    return this.placeholder_fields?.includes(fieldName) ?? false;
  }

  public hasPlaceholderName(): boolean {
    return (
      this.hasPlaceholderField('first_name') ||
      this.hasPlaceholderField('last_name')
    );
  }

  public isPatientMasked(): boolean {
    return this.last_name === 'MASKED';
  }

  get initials(): string {
    return `${this.first_name?.[0] ?? ''}${
      this.last_name?.[0] ?? ''
    }`.toUpperCase();
  }

  get birthdayAndAge(): string {
    if (!this.dob) {
      return 'No Birthday';
    }
    const birthday = dayjs(this.dob).format('MM/DD/YYYY');
    const age = this.getAgeInYears();
    return `${birthday} (${age})`;
  }

  get facilityName(): string {
    return this.facility?.name ?? '';
  }

  /**
   * Facility that owns the referral platform integration for this lead. For
   * moved/exchanged leads this stays the facility the referral came into, while
   * `facility_id` points at the facility the lead now lives in.
   */
  get integrationFacilityId(): string {
    return this.original_facility_id ?? this.facility_id;
  }

  get isAcceptedOrDeclined(): boolean {
    return (
      this.status?.name === LeadStatusName.YesAccept ||
      this.status?.name === LeadStatusName.NoDecline
    );
  }

  public static sortLeadsByFacility(
    currentFacilityId: string | null
  ): (a: LeadModel, b: LeadModel) => number {
    return (a: LeadModel, b: LeadModel) => {
      if (
        a.facility_id === currentFacilityId &&
        b.facility_id !== currentFacilityId
      ) {
        return -1;
      }
      if (
        a.facility_id !== currentFacilityId &&
        b.facility_id === currentFacilityId
      ) {
        return 1;
      }
      return 0;
    };
  }

  public shouldDisplayForCurrentFacility(
    currentFacilityId: string | null,
    displayAllLeads: boolean
  ): boolean {
    return (
      this.claimed_by_facility?.id === currentFacilityId &&
      this.facility_id !== currentFacilityId &&
      !displayAllLeads
    );
  }

  public shouldHideActions(
    currentFacilityId: string | null,
    isAllFacilitiesLeadsSelected: boolean
  ): boolean {
    if (isAllFacilitiesLeadsSelected) {
      return false;
    }
    return this.facility_id !== currentFacilityId;
  }

  public hasDuplicateActions(duplicateGroupIdCount: number): boolean {
    return (
      duplicateGroupIdCount > 1 &&
      !this.claimed_by_facility &&
      !this.is_cancelled
    );
  }

  public getTimeSinceReceived(): string {
    return getFromNowFormattedDate(this.lead_received_date);
  }

  public getPccPushByFullName(): string {
    return `${this.pcc_push_by_user?.first_name} ${this.pcc_push_by_user?.last_name}`;
  }

  public visualReferencesReady(): boolean {
    return (
      // for backward compatibility (the flag was added later)
      !!this.is_visual_references_ready ||
      !!this.analysis_results?.some((item) => item.visual_references?.length)
    );
  }

  private isResidentStatus(status?: string) {
    return (
      status != null &&
      (
        [
          AdmissionStatus.Booked,
          AdmissionStatus.MovedIn,
          AdmissionStatus.Hospitalized,
          AdmissionStatus.MovedOut,
          AdmissionStatus.Loa,
          AdmissionStatus.Admitted,
          AdmissionStatus.Transferred,
          AdmissionStatus.Discharged,
          AdmissionStatus.Deceased,

          // Canonical Admission Status Codes
          AdmissionStatusCode.Booked,
          AdmissionStatusCode.MovedIn,
          AdmissionStatusCode.Hospitalized,
          AdmissionStatusCode.MovedOut,
          AdmissionStatusCode.Loa,
          AdmissionStatusCode.Admitted,
          AdmissionStatusCode.Transferred,
          AdmissionStatusCode.Discharged,
          AdmissionStatusCode.Deceased
        ] as string[]
      ).includes(status)
    );
  }

  public isReviewStageClickable(): boolean {
    const isAccepted = this.status?.name === LeadStatusName.YesAccept;

    const isResident =
      this.isResidentStatus(this.admission_status) ||
      this.isResidentStatus(this.admission_status_code);

    return !(isAccepted && isResident);
  }

  public hasCombinableConnector(): boolean {
    return combinableConnectors.includes(this.connector as LeadConnectorEnum);
  }

  public canBeCombinedAndCancelled(currentFacilityId: string): boolean {
    if (
      this.is_cancelled ||
      this.admission_status ||
      this.facility_id !== currentFacilityId
    ) {
      return false;
    }
    return this.hasCombinableConnector();
  }

  public getCannotCombineReason(): string | null {
    if (this.admission_status) {
      return 'Unable to combine and cancel referral with an Admission Status';
    }
    if (!this.hasCombinableConnector()) {
      return `Unable to combine and cancel referrals from ${this.connector || 'this platform'}`;
    }
    return null;
  }

  public getCannotExchangeReason(): string | null {
    if (this.connector === LeadConnectorEnum.Roc) {
      return 'Cannot move a referral received through exacare ai';
    }
    if (
      this.admission_status ||
      (this.admission_status_code &&
        this.admission_status_code !== AdmissionStatusCode.None)
    ) {
      return 'Cannot move a referral if it has an Admission Status';
    }
    return null;
  }

  public hasSpecialCriteria(): boolean {
    return (this.special_criteria ?? []).length > 0;
  }

  public hasSexOffenderSpecialCriteria(): boolean {
    return (
      this.special_criteria?.find(
        (item) =>
          item.identifier ===
          ScreenerSpecialCriteriaIdentifierEnum.SexOffenderCheck
      ) !== undefined
    );
  }

  public getPrimaryInsurance(): ScreenerInsuranceModel | undefined {
    return this.insurances?.find((item) => item.isPrimary());
  }

  public hasPackageLimitWarning(): boolean {
    return (
      this.lead_warnings?.some(
        (w) => w.type === LeadWarningTypeEnum.PackageLimitExceeded
      ) ?? false
    );
  }

  public getAgeInYears(): number | null {
    if (!this.dob) {
      return null;
    }
    return dayjs().diff(dayjs(this.dob), 'years');
  }

  public static connectorToIntegrationType(
    connectorType: LeadConnectorEnum
  ): FacilityIntegrationNameEnum {
    switch (connectorType) {
      case LeadConnectorEnum.WellSkyIntake:
        return FacilityIntegrationNameEnum.WellskyIntake;
      case LeadConnectorEnum.WellSkyReferralManagement:
        return FacilityIntegrationNameEnum.WellskyReferralManagement;
      case LeadConnectorEnum.WellSkyIntakeCarePortQuickCase:
        return FacilityIntegrationNameEnum.WellskyIntakeCareportQuickcase;
      case LeadConnectorEnum.WellSkyCarePortReferralIntake:
        return FacilityIntegrationNameEnum.WellskyCareportReferralIntake;
      case LeadConnectorEnum.Ensocare:
        return FacilityIntegrationNameEnum.Ensocare;
      case LeadConnectorEnum.Aidin:
        return FacilityIntegrationNameEnum.Aidin;
      case LeadConnectorEnum.Aida:
        return FacilityIntegrationNameEnum.Aida;
      case LeadConnectorEnum.EpicCareLink:
        return FacilityIntegrationNameEnum.EpicCareLink;
      case LeadConnectorEnum.EpicCareLinkAlbanyMed:
        return FacilityIntegrationNameEnum.EpicCareLinkAlbanyMed;
      case LeadConnectorEnum.EpicCareLinkAllinaHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkAllinaHealth;
      case LeadConnectorEnum.EpicCareLinkAscension:
        return FacilityIntegrationNameEnum.EpicCareLinkAscension;
      case LeadConnectorEnum.EpicCareLinkAsante:
        return FacilityIntegrationNameEnum.EpicCareLinkAsante;
      case LeadConnectorEnum.EpicCareLinkAspirusHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkAspirusHealth;
      case LeadConnectorEnum.EpicCareLinkAtriumHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkAtriumHealth;
      case LeadConnectorEnum.EpicCareLinkBaptistHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkBaptistHealth;
      case LeadConnectorEnum.EpicCareLinkBaptistOneCare:
        return FacilityIntegrationNameEnum.EpicCareLinkBaptistOneCare;
      case LeadConnectorEnum.EpicCareLinkBenefisHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkBenefisHealthSystem;
      case LeadConnectorEnum.EpicCareLinkBaylorScottWhiteHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkBaylorScottWhiteHealth;
      case LeadConnectorEnum.EpicCareLinkBostonMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkBostonMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkBryanHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkBryanHealth;
      case LeadConnectorEnum.EpicCareLinkBsaHospital:
        return FacilityIntegrationNameEnum.EpicCareLinkBsaHospital;
      case LeadConnectorEnum.EpicCareLinkCapeCodHealthcare:
        return FacilityIntegrationNameEnum.EpicCareLinkCapeCodHealthcare;
      case LeadConnectorEnum.EpicCareLinkCaroMont:
        return FacilityIntegrationNameEnum.EpicCareLinkCaromont;
      case LeadConnectorEnum.EpicCareLinkChristusHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkChristusHealth;
      case LeadConnectorEnum.EpicCareLinkConeHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkConeHealth;
      case LeadConnectorEnum.EpicCareLinkCovenantHealthCare:
        return FacilityIntegrationNameEnum.EpicCareLinkCovenantHealthcare;
      case LeadConnectorEnum.EpicCareLinkBonSecoursMercyHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkBonSecoursMercyHealth;
      case LeadConnectorEnum.EpicCareLinkDenverHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkDenverHealth;
      case LeadConnectorEnum.EpicCareLinkDukeHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkDukeHealth;
      case LeadConnectorEnum.EpicCareLinkEcuHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkEcuHealth;
      case LeadConnectorEnum.EpicCareLinkElCaminoHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkElCaminoHealth;
      case LeadConnectorEnum.EpicCareLinkDeaconess:
        return FacilityIntegrationNameEnum.EpicCareLinkDeaconess;
      case LeadConnectorEnum.EpicCareLinkEnloeHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkEnloeHealth;
      case LeadConnectorEnum.EpicCareLinkFirstHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkFirstHealth;
      case LeadConnectorEnum.EpicCareLinkGenesisHealthcareSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkGenesisHealthcareSystem;
      case LeadConnectorEnum.EpicCareLinkGeisinger:
        return FacilityIntegrationNameEnum.EpicCareLinkGeisinger;
      case LeadConnectorEnum.EpicCareLinkCheyenneRegionalMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkCheyenneRegionalMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkHartfordHealthCare:
        return FacilityIntegrationNameEnum.EpicCareLinkHartfordHealthcare;
      case LeadConnectorEnum.EpicCareLinkHackensackMeridianHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkHackensackMeridianHealth;
      case LeadConnectorEnum.EpicCareLinkHealthPartners:
        return FacilityIntegrationNameEnum.EpicCareLinkHealthPartners;
      case LeadConnectorEnum.EpicCareLinkHealtheConnection:
        return FacilityIntegrationNameEnum.EpicCareLinkHealtheConnection;
      case LeadConnectorEnum.EpicCareLinkHillcrestHealthCareSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkHillcrestHealthcare;
      case LeadConnectorEnum.EpicCareLinkHurleyMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkHurleyMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkIowaHealthCare:
        return FacilityIntegrationNameEnum.EpicCareLinkIowaHealthCare;
      case LeadConnectorEnum.EpicCareLinkIntegrisHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkIntegrisHealth;
      case LeadConnectorEnum.EpicCareLinkLegacyHealthyPlanet:
        return FacilityIntegrationNameEnum.EpicCareLinkLegacyHealthyPlanet;
      case LeadConnectorEnum.EpicCareLinkKaiserPermanente:
        return FacilityIntegrationNameEnum.EpicCareLinkKaiserPermanente;
      case LeadConnectorEnum.EpicCareLinkKingsDaughtersHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkKingsDaughters;
      case LeadConnectorEnum.EpicCareLinkHennepinCountyMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkHennepin;
      case LeadConnectorEnum.EpicCareLinkHospitalSistersHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkHospitalSistersHealthSystem;
      case LeadConnectorEnum.EpicCareLinkMHealthFairview:
        return FacilityIntegrationNameEnum.EpicCareLinkMHealthFairview;
      case LeadConnectorEnum.EpicCareLinkMainLineHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkMainLineHealth;
      case LeadConnectorEnum.EpicCareLinkMayoClinic:
        return FacilityIntegrationNameEnum.EpicCareLinkMayoClinic;
      case LeadConnectorEnum.EpicCareLinkMercy:
        return FacilityIntegrationNameEnum.EpicCareLinkMercy;
      case LeadConnectorEnum.EpicCareLinkMethodistHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkMethodistHealthSystem;
      case LeadConnectorEnum.EpicCareLinkMethodistHospitals:
        return FacilityIntegrationNameEnum.EpicCareLinkMethodistHospitals;
      case LeadConnectorEnum.EpicCareLinkMiddlesexHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkMiddlesexHealth;
      case LeadConnectorEnum.EpicCareLinkNch:
        return FacilityIntegrationNameEnum.EpicCareLinkNch;
      case LeadConnectorEnum.EpicCareLinkNortonHealthcare:
        return FacilityIntegrationNameEnum.EpicCareLinkNortonHealthcare;
      case LeadConnectorEnum.EpicCareLinkNorthMemorialHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkNorthMemorialHealth;
      case LeadConnectorEnum.EpicCareLinkNorthMississippiHealthServices:
        return FacilityIntegrationNameEnum.EpicCareLinkNorthMississippiHealthServices;
      case LeadConnectorEnum.EpicCareLinkMyMichiganHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkMyMichiganHealth;
      case LeadConnectorEnum.EpicCareLinkOklahomaHeartHospital:
        return FacilityIntegrationNameEnum.EpicCareLinkOklahomaHeartHospital;
      case LeadConnectorEnum.EpicCareLinkOwensboroHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkOwensboroHealth;
      case LeadConnectorEnum.EpicCareLinkThedaCare:
        return FacilityIntegrationNameEnum.EpicCareLinkThedacare;
      case LeadConnectorEnum.EpicCareLinkPiedmont:
        return FacilityIntegrationNameEnum.EpicCareLinkPiedmont;
      case LeadConnectorEnum.EpicCareLinkWakeMed:
        return FacilityIntegrationNameEnum.EpicCareLinkWakeMed;
      case LeadConnectorEnum.EpicCareLinkMountainsideMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkMountainsideMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkGuthrie:
        return FacilityIntegrationNameEnum.EpicCareLinkGuthrie;
      case LeadConnectorEnum.EpicCareLinkRwjBarnabas:
        return FacilityIntegrationNameEnum.EpicCareLinkRwjBarnabas;
      case LeadConnectorEnum.EpicCareLinkPortneuf:
        return FacilityIntegrationNameEnum.EpicCareLinkPortneuf;
      case LeadConnectorEnum.EpicCareLinkRochesterRegionalHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkRochesterRegionalHealth;
      case LeadConnectorEnum.EpicCareLinkOregonHealthScienceUniversity:
        return FacilityIntegrationNameEnum.EpicCareLinkOhsu;
      case LeadConnectorEnum.EpicCareLinkLcmcHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkLcmcHealth;
      case LeadConnectorEnum.EpicCareLinkLovelaceHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkLovelaceHealthSystem;
      case LeadConnectorEnum.EpicCareLinkAtlanticHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkAtlanticHealth;
      case LeadConnectorEnum.EpicCareLinkGundersenHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkGundersenHealth;
      case LeadConnectorEnum.EpicCareLinkProvidence:
        return FacilityIntegrationNameEnum.EpicCareLinkProvidence;
      case LeadConnectorEnum.EpicCareLinkProvidenceWashington:
        return FacilityIntegrationNameEnum.EpicCareLinkProvidenceWashington;
      case LeadConnectorEnum.EpicCareLinkJupiterMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkJupiterMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkJpsHealthNetwork:
        return FacilityIntegrationNameEnum.EpicCareLinkJpsHealthNetwork;
      case LeadConnectorEnum.EpicCareLinkSaintFrancis:
        return FacilityIntegrationNameEnum.EpicCareLinkSaintFrancis;
      case LeadConnectorEnum.EpicCareLinkSouthcoast:
        return FacilityIntegrationNameEnum.EpicCareLinkSouthcoast;
      case LeadConnectorEnum.EpicCareLinkSouthShoreHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkSouthShoreHealth;
      case LeadConnectorEnum.EpicCareLinkSsmHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkSsmHealth;
      case LeadConnectorEnum.EpicCareLinkStormontVailHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkStormontVailHealth;
      case LeadConnectorEnum.EpicCareLinkUcHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUcHealth;
      case LeadConnectorEnum.EpicCareLinkTexasHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkTexasHealth;
      case LeadConnectorEnum.EpicCareLinkTheQueensHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkTheQueensHealthSystem;
      case LeadConnectorEnum.EpicCareLinkTriHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkTrihealth;
      case LeadConnectorEnum.EpicCareLinkTrinityHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkTrinityHealth;
      case LeadConnectorEnum.EpicCareLinkUConnHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUconnHealth;
      case LeadConnectorEnum.EpicCareLinkUncHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUncHealth;
      case LeadConnectorEnum.EpicCareLinkUnityPointHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUnityPointHealth;
      case LeadConnectorEnum.EpicCareLinkUniversityOfCincinnati:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityOfCincinnati;
      case LeadConnectorEnum.EpicCareLinkUniversityOfKansas:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityOfKansas;
      case LeadConnectorEnum.EpicCareLinkUniversityOfUtah:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityOfUtah;
      case LeadConnectorEnum.EpicCareLinkUniversityMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkCapeFearValley:
        return FacilityIntegrationNameEnum.EpicCareLinkCapeFearValley;
      case LeadConnectorEnum.EpicCareLinkOchsnerHealthNetwork:
        return FacilityIntegrationNameEnum.EpicCareLinkOchsnerHealthNetwork;
      case LeadConnectorEnum.EpicCareLinkUniversityOfMississippiMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkUmmc;
      case LeadConnectorEnum.EpicCareLinkAscensionWi:
        return FacilityIntegrationNameEnum.EpicCareLinkAscensionWi;
      case LeadConnectorEnum.EpicCareLinkUpmcPinnacle:
        return FacilityIntegrationNameEnum.EpicCareLinkUpmcPinnacle;
      case LeadConnectorEnum.EpicCareLinkUtHealthEastTexas:
        return FacilityIntegrationNameEnum.EpicCareLinkUtHealthEastTexas;
      case LeadConnectorEnum.EpicCareLinkUtSouthwesternMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkUtSouthwestern;
      case LeadConnectorEnum.EpicCareLinkVitruvianHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkVitruvianHealth;
      case LeadConnectorEnum.EpicCareLinkWellstar:
        return FacilityIntegrationNameEnum.EpicCareLinkWellstar;
      case LeadConnectorEnum.WellSkyReferralManagementWebReferral:
        return FacilityIntegrationNameEnum.WellskyReferralManagementWebReferral;
      case LeadConnectorEnum.EnsocareVa:
        return FacilityIntegrationNameEnum.EnsocareVa;
      case LeadConnectorEnum.EpicCareLinkPremierHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkPremierHealth;
      case LeadConnectorEnum.EpicCareLinkStLukes:
        return FacilityIntegrationNameEnum.EpicCareLinkStLukes;
      case LeadConnectorEnum.EpicCareLinkParkview:
        return FacilityIntegrationNameEnum.EpicCareLinkParkview;
      case LeadConnectorEnum.EpicCareLinkFmolhsCareConnect:
        return FacilityIntegrationNameEnum.EpicCareLinkFmolhsCareConnect;
      case LeadConnectorEnum.EpicCareLinkOhioHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkOhioHealth;
      case LeadConnectorEnum.EpicCareLinkShannonHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkShannonHealth;
      case LeadConnectorEnum.EpicCareLinkBronsonHealthcare:
        return FacilityIntegrationNameEnum.EpicCareLinkBronsonHealthcare;
      case LeadConnectorEnum.EpicCareLinkCentraCare:
        return FacilityIntegrationNameEnum.EpicCareLinkCentraCare;
      case LeadConnectorEnum.EpicCareLinkOlmstedMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkOmc;
      case LeadConnectorEnum.EpicCareLinkFranciscanHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkFranciscanHealth;
      case LeadConnectorEnum.EpicCareLinkMohawkValleyHealthSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkMvhs;
      case LeadConnectorEnum.EpicCareLinkUniversityOfMarylandMedicalSystem:
        return FacilityIntegrationNameEnum.EpicCareLinkUmms;
      case LeadConnectorEnum.EpicCareLinkUniversityHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityHealth;
      case LeadConnectorEnum.EpicCareLinkSingingRiver:
        return FacilityIntegrationNameEnum.EpicCareLinkSingingRiver;
      case LeadConnectorEnum.EpicCareLinkOsfHealthCare:
        return FacilityIntegrationNameEnum.EpicCareLinkOsfHealthcare;
      case LeadConnectorEnum.EpicCareLinkStElizabethHealthcare:
        return FacilityIntegrationNameEnum.EpicCareLinkStElizabethHealthcare;
      case LeadConnectorEnum.EpicCareLinkCarolinaEast:
        return FacilityIntegrationNameEnum.EpicCareLinkCarolinaEast;
      case LeadConnectorEnum.EpicCareLinkErlanger:
        return FacilityIntegrationNameEnum.EpicCareLinkErlanger;
      case LeadConnectorEnum.EpicCareLinkUniversityOfKansasStFrancis:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityOfKansasStFrancis;
      case LeadConnectorEnum.EpicCareLinkSelectMedical:
        return FacilityIntegrationNameEnum.EpicCareLinkSelectMedical;
      case LeadConnectorEnum.EpicCareLinkLexingtonMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkLexingtonMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkEssentiaHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkEssentiaHealth;
      case LeadConnectorEnum.EpicCareLinkMedicalUniversityOfSouthCarolina:
        return FacilityIntegrationNameEnum.EpicCareLinkMusc;
      case LeadConnectorEnum.EpicCareLinkUwHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkUwHealth;
      case LeadConnectorEnum.EpicCareLinkUniversityOfRochesterMedicine:
        return FacilityIntegrationNameEnum.EpicCareLinkUniversityOfRochesterMedicine;
      case LeadConnectorEnum.EpicCareLinkSalemHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkSalemHealth;
      case LeadConnectorEnum.EpicCareLinkBalladHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkBalladHealth;
      case LeadConnectorEnum.EpicCareLinkGreatRiver:
        return FacilityIntegrationNameEnum.EpicCareLinkGreatRiver;
      case LeadConnectorEnum.EpicCareLinkUkHealthCare:
        return FacilityIntegrationNameEnum.EpicCareLinkUkHealthcare;
      case LeadConnectorEnum.EpicCareLinkMosaicLifeCare:
        return FacilityIntegrationNameEnum.EpicCareLinkMosaicLifeCare;
      case LeadConnectorEnum.EpicCareLinkCghMedicalCenter:
        return FacilityIntegrationNameEnum.EpicCareLinkCghMedicalCenter;
      case LeadConnectorEnum.EpicCareLinkLickingMemorialHealth:
        return FacilityIntegrationNameEnum.EpicCareLinkLickingMemorialHealth;
      case LeadConnectorEnum.Repisodic:
        return FacilityIntegrationNameEnum.Repisodic;
      default:
        throw new Error(
          `Cannot convert connector [${connectorType}] to FacilityIntegrationNameEnum`
        );
    }
  }
}
